import { ref } from "vue";
import { router } from "@inertiajs/vue3";

/**
 * useInertiaAction (GH-1159)
 *
 * For controls whose effect only becomes visible after a follow-up partial
 * reload — bookmark chips, follow buttons, list deletes. The busy flag spans
 * the request *and* the reload, so the control lands directly in its new
 * state instead of resetting for a beat while the old props are still on
 * screen. Repeat clicks while busy are ignored.
 *
 * @returns {{ busy: import("vue").Ref<boolean>, run: Function }}
 *   `run(request, options)` — `request` is a promise or a function returning
 *   one (the axios call). Options: `only` (prop names for the partial reload;
 *   omit to reload everything), `preserveScroll` (default true), `onSuccess`
 *   (after the request, before the reload — apply optimistic state here),
 *   `onError`. Resolves true when both request and reload completed.
 */
export function useInertiaAction() {
    const busy = ref(false);

    async function run(request, options = {}) {
        const {
            only = null,
            preserveScroll = true,
            onSuccess = null,
            onError = null,
        } = options;

        if (busy.value) {
            return false;
        }

        busy.value = true;

        try {
            await (typeof request === "function" ? request() : request);
        } catch (error) {
            console.error(error);
            busy.value = false;
            onError?.(error);

            return false;
        }

        onSuccess?.();

        // onFinish fires whether the reload succeeded or failed, so busy
        // always clears once Inertia is done.
        await new Promise((resolve) => {
            router.reload({
                ...(only ? { only } : {}),
                preserveScroll,
                onFinish: resolve,
            });
        });

        busy.value = false;

        return true;
    }

    return { busy, run };
}
